/**
 * The advanced model search (jorigin/brewlytics#2289): `POST api/models/query`.
 *
 * The body is a structured, allow-listed query; the server never takes a
 * field name or an operator from the client. Its limits, mirrored here so the
 * UI can say what is wrong before it asks (`ModelQueryValidator.java` is the
 * authority):
 *  - tags: at most 10 per bucket, each `[\p{L}\p{N} _.:/+#&'-]{1,64}`;
 *  - authors: at most 10, each `"me"` or a profile id;
 *  - functionals and submodels (#2299): groups, `[[a, b], [c]]` is (a and b)
 *    or c; at most 5 groups of at most 10 ids, none empty; the old single
 *    `usesFunctional` still reads as `[[id]]`;
 *  - text: at most 200 characters;
 *  - `within`: 7d, 30d, 90d or 1y; `from`/`to`: ISO days or zoned date-times,
 *    not more than 10 years back, `to` exclusive (a day-only `to` covers
 *    that whole day, UTC);
 *  - `limit` at most 50, `skip` at most 10000, the body under 4096 bytes.
 *
 * The answer carries `hasMore` (the server fetches limit+1), never a total.
 * A 400 is `{"error": "invalid query", "problems": ["query.tags.all[0]: …"]}`,
 * thrown here as `InvalidModelQueryError` so the caller can show the
 * problems instead of a generic failure.
 */
import axios from 'axios';
import apiClient from './client';
import type { Model } from '@/types';
import type { ModelQuery } from '@/types/modelQuery';

export * from '@/types/modelQuery';

export interface ModelQueryOptions {
  skip?: number;
  limit?: number;
  /** Brief summaries (no avatar etc.); the results list wants full ones. */
  brief?: boolean;
  signal?: AbortSignal;
}

export interface ModelQueryPage {
  data: Model[];
  skip: number;
  limit: number;
  hasMore: boolean;
}

/** The server refused the query (400): `problems` names each, by key. */
export class InvalidModelQueryError extends Error {
  readonly problems: string[];

  constructor(problems: string[]) {
    super(problems.length ? problems.join('; ') : 'invalid query');
    this.name = 'InvalidModelQueryError';
    this.problems = problems;
  }
}

/** Run an advanced model search. Throws `InvalidModelQueryError` on a 400. */
export async function queryModels(query: ModelQuery, options: ModelQueryOptions = {}): Promise<ModelQueryPage> {
  const { skip = 0, limit = 24, brief = false, signal } = options;
  try {
    const res = await apiClient.post<Partial<ModelQueryPage>>(
      'api/models/query',
      { query, skip, limit, brief },
      signal ? { signal } : undefined,
    );
    const body = res.data ?? {};
    return {
      data: Array.isArray(body.data) ? body.data : [],
      skip: body.skip ?? skip,
      limit: body.limit ?? limit,
      hasMore: body.hasMore === true,
    };
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 400) {
      const body = err.response.data as { problems?: unknown } | string | undefined;
      const parsed = typeof body === 'string' ? safeParse(body) : body;
      const problems = Array.isArray(parsed?.problems)
        ? parsed.problems.filter((p): p is string => typeof p === 'string')
        : [];
      throw new InvalidModelQueryError(problems);
    }
    throw err;
  }
}

function safeParse(s: string): { problems?: unknown } | undefined {
  try {
    return JSON.parse(s) as { problems?: unknown };
  } catch {
    return undefined;
  }
}
